/**
 * Modulo "Facturas sin clasificar"
 * Muestra las FacturaProyecto que se asignaron automaticamente al primer
 * capitulo del proyecto durante la migracion de huerfanas (capitulo_id NULL).
 * Permite al usuario re-asignar el capitulo correcto a mano.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import './FacturasSinClasificar.css';

const QUERY = `query {
  facturasSinClasificar {
    total
    monto_total
    facturas {
      fp_id factura_id numero_factura fecha_emision monto valor_asignado
      proyecto_id proyecto_nombre apartamento nombre_cliente
      capitulo_id capitulo_nombre proveedor_nombre proveedor_nit estado
      capitulos_disponibles { id numero nombre }
    }
  }
}`;

const MUT = `mutation($fp_id: Int!, $capitulo_id: Int!) {
  reasignarCapituloFactura(fp_id: $fp_id, capitulo_id: $capitulo_id) { success message }
}`;

const fmt = (n) => new Intl.NumberFormat('es-CO', {
  style: 'currency', currency: 'COP', minimumFractionDigits: 0,
}).format(n || 0);

const FacturasSinClasificar = () => {
  const { success, error: showError } = useNotification();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [seleccion, setSeleccion] = useState({}); // { [fp_id]: capitulo_id }
  const [guardandoId, setGuardandoId] = useState(null);
  const [filtroProyecto, setFiltroProyecto] = useState('');

  const cargar = async () => {
    try {
      setLoading(true);
      const r = await client.query(QUERY);
      setData(r.facturasSinClasificar);
    } catch (e) {
      showError('Error al cargar: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, []);

  const onChangeCap = (fpId, capId) => {
    setSeleccion(prev => ({ ...prev, [fpId]: capId }));
  };

  const reasignar = async (fpId) => {
    const capId = parseInt(seleccion[fpId]);
    if (!capId) { showError('Selecciona un capítulo destino'); return; }

    try {
      setGuardandoId(fpId);
      const r = await client.mutate(MUT, { fp_id: fpId, capitulo_id: capId });
      if (r.reasignarCapituloFactura.success) {
        success('Capítulo re-asignado');
        // Quitar de la lista local (ya no aparece como sin clasificar)
        setData(prev => prev ? {
          ...prev,
          total: prev.total - 1,
          monto_total: prev.monto_total - (prev.facturas.find(f => f.fp_id === fpId)?.valor_asignado || 0),
          facturas: prev.facturas.filter(f => f.fp_id !== fpId),
        } : prev);
        setSeleccion(prev => { const n = { ...prev }; delete n[fpId]; return n; });
      } else {
        showError(r.reasignarCapituloFactura.message);
      }
    } catch (e) {
      showError('Error: ' + e.message);
    } finally {
      setGuardandoId(null);
    }
  };

  const filtradas = useMemo(() => {
    if (!data) return [];
    const t = busqueda.trim().toLowerCase();
    return data.facturas.filter(f => {
      if (filtroProyecto && f.proyecto_id !== parseInt(filtroProyecto)) return false;
      if (!t) return true;
      const blob = `${f.numero_factura} ${f.proyecto_nombre || ''} ${f.apartamento || ''} ${f.nombre_cliente || ''} ${f.proveedor_nombre || ''}`.toLowerCase();
      return blob.includes(t);
    });
  }, [data, busqueda, filtroProyecto]);

  // Lista unica de proyectos para el filtro
  const proyectosUnicos = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    for (const f of data.facturas) {
      if (!map.has(f.proyecto_id)) {
        map.set(f.proyecto_id, { id: f.proyecto_id, nombre: `${f.proyecto_nombre || ''}${f.apartamento ? ' - Apto ' + f.apartamento : ''}` });
      }
    }
    return [...map.values()].sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [data]);

  if (loading) return <div className="fsc-loading">Cargando…</div>;
  if (!data) return <div className="fsc-loading">Sin datos.</div>;

  return (
    <div className="fsc-root">
      <div className="fsc-banner">
        <div>
          <strong>{data.total}</strong> facturas migradas al capítulo "Sin clasificar".
          Re-asígnalas al capítulo correcto cuando puedas.
          <div className="fsc-banner-monto">Total: {fmt(data.monto_total)}</div>
        </div>
      </div>

      <div className="fsc-filtros">
        <input
          type="text"
          placeholder="Buscar por factura, proyecto, apto, cliente o proveedor..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          className="fsc-search"
        />
        <select value={filtroProyecto} onChange={e => setFiltroProyecto(e.target.value)} className="fsc-select-filtro">
          <option value="">Todos los proyectos ({proyectosUnicos.length})</option>
          {proyectosUnicos.map(p => (
            <option key={p.id} value={p.id}>{p.nombre}</option>
          ))}
        </select>
      </div>

      {filtradas.length === 0 ? (
        <div className="fsc-empty">
          {data.total === 0
            ? '🎉 No quedan facturas sin clasificar.'
            : 'No hay facturas que coincidan con el filtro.'}
        </div>
      ) : (
        <div className="fsc-tabla-wrap">
          <table className="fsc-tabla">
            <thead>
              <tr>
                <th>Factura</th>
                <th>Proveedor</th>
                <th>Proyecto / Apto</th>
                <th>Valor asignado</th>
                <th>Estado</th>
                <th>Capítulo actual</th>
                <th>Re-asignar a</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtradas.map(f => (
                <tr key={f.fp_id}>
                  <td>
                    <div className="fsc-fact-num">{f.numero_factura}</div>
                    <div className="fsc-fact-sub">{f.fecha_emision || ''}</div>
                  </td>
                  <td>
                    <div>{f.proveedor_nombre || '—'}</div>
                    <div className="fsc-fact-sub">{f.proveedor_nit || ''}</div>
                  </td>
                  <td>
                    <div>{f.proyecto_nombre || '—'}</div>
                    <div className="fsc-fact-sub">
                      {f.apartamento ? `Apto ${f.apartamento}` : ''}
                      {f.nombre_cliente ? ` · ${f.nombre_cliente}` : ''}
                    </div>
                  </td>
                  <td className="fsc-valor">{fmt(f.valor_asignado)}</td>
                  <td>
                    <span className={`fsc-badge fsc-badge-${(f.estado || '').replace('_', '-')}`}>
                      {f.estado || '—'}
                    </span>
                  </td>
                  <td className="fsc-cap-actual">{f.capitulo_nombre || '—'}</td>
                  <td>
                    <select
                      value={seleccion[f.fp_id] || ''}
                      onChange={e => onChangeCap(f.fp_id, e.target.value)}
                      className="fsc-select-cap"
                    >
                      <option value="">Selecciona capítulo...</option>
                      {f.capitulos_disponibles
                        .filter(c => c.id !== f.capitulo_id)
                        .map(c => (
                          <option key={c.id} value={c.id}>{c.nombre}</option>
                        ))}
                    </select>
                  </td>
                  <td>
                    <button
                      className="fsc-btn-guardar"
                      disabled={!seleccion[f.fp_id] || guardandoId === f.fp_id}
                      onClick={() => reasignar(f.fp_id)}
                    >
                      {guardandoId === f.fp_id ? '...' : 'Guardar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FacturasSinClasificar;
